import React from 'react';
import clsx from 'clsx';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {type RowData} from '@tanstack/react-table';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {TableBodyRow, type TableBodyRowProps} from './TableBodyRow';
import {
	getTableBodyRowDraggableUtilityClass,
	TABLE_BODY_ROW_DRAGGABLE_CLASS_NAME,
	tableBodyRowDraggableClasses,
	type TableBodyRowDraggableClasses,
} from './tableBodyRowDraggableClasses';

type TableBodyRowDraggableOwnerState = WithClasses<
	TableBodyRowDraggableClasses,
	{
		isDragGhost?: boolean;
		isDragging?: boolean;
		shifted?: boolean;
	}
>;

const useUtilityClasses = (ownerState: TableBodyRowDraggableOwnerState) => {
	const {classes, isDragGhost, isDragging, shifted} = ownerState;
	const slots = {
		root: [
			'root',
			isDragGhost && 'isDragGhost',
			isDragging && 'isDragging',
			shifted && 'shifted',
		],
	};
	return composeClasses(slots, getTableBodyRowDraggableUtilityClass, classes);
};

const TableBodyRowDraggableRoot = styled(TableBodyRow, {
	name: TABLE_BODY_ROW_DRAGGABLE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	// this is to address the empty space that the dragged row will leave behind
	// this will make sure there is a border above and below the empty space of the dragging row
	// see note below about us hiding the dragging row
	[`&.${tableBodyRowDraggableClasses.shifted}`]: {
		// borderTop: `1px solid ${theme.palette.custom.border}`,
	},
	// hide while dragging - eventually the data would dissapear anyways due to virtualziation
	// we use a drag overlay to show the dragged row and all we need is a nice empty space reflected here
	[`&.${tableBodyRowDraggableClasses.isDragging}`]: {
		visibility: 'hidden',
	},
	// make sure no matter where the cursor falls in the row, it's always a grabbing cursor
	[`&.${tableBodyRowDraggableClasses.isDragGhost}`]: {
		cursor: 'grabbing !important',
		'& > div': {
			pointerEvents: 'none',
		},
	},
})) as typeof TableBodyRow;

export interface TableBodyRowDraggableProps<TData extends RowData>
	extends TableBodyRowProps<TData> {
	disableDrag?: boolean;
	isDragGhost?: boolean;
}

export const TableBodyRowDraggablePrimitive = <TData extends RowData>(
	{
		classes: _classes,
		className,
		disableDrag,
		isDragGhost,
		...props
	}: TableBodyRowDraggableProps<TData>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	// setup the sortable hook
	const {
		active,
		isDragging,
		isSorting,
		overIndex,
		newIndex,
		setNodeRef,
		transform,
		transition,
	} = useSortable({
		disabled: disableDrag || isDragGhost,
		id: props.row.id,
	});

	// we need to use a shared ref to support both the outside and the sortable hook
	const rootRef = useSharedRef(null, [ref, setNodeRef]);

	// generate drag styles
	const dragStyle = {
		transform: CSS.Translate.toString(transform),
		// we only want to animate the drag if the row is active
		// this allows for other transitions to happen when not dragging
		transition: active ? transition : undefined,
	} as React.CSSProperties;

	const ownerState: TableBodyRowDraggableOwnerState = {
		classes: _classes,
		className,
		isDragGhost,
		isDragging,
		shifted:
			!disableDrag &&
			isSorting &&
			(overIndex + 1 === props.index || props.index < newIndex),
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<TableBodyRowDraggableRoot<TData>
			{...props}
			ref={rootRef}
			className={clsx(classes.root, className)}
			style={{
				...props.style,
				...dragStyle,
				transition: dragStyle.transition ?? props.style?.transition,
			}}
		/>
	);
};

/**
 * A draggable row for the table body.
 *
 * This component is a wrapper around the `TableBodyRow` component that adds drag and drop functionality to the row.
 *
 * @typeParam TData The type of data that represents a row
 *
 * @example
 * ```tsx
 * <TableBodyRowDraggable
 * 	row={row}
 * 	index={index}
 * 	disableDrag={disableDrag}
 * 	isDragGhost={isDragGhost}
 * 	onClick={handleRowClick}
 * />
 * ```
 */
export const TableBodyRowDraggable = React.memo(
	React.forwardRef(TableBodyRowDraggablePrimitive),
) as <TData extends RowData>(
	props: TableBodyRowDraggableProps<TData>,
) => JSX.Element;

export default TableBodyRowDraggable;
